import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { usePermissions } from '../../hooks/usePermissions';
import ExportButton from '../../components/ExportButton';
import { 
  Plus, ChevronLeft, ChevronRight, Edit, Trash2, Copy, Eye,
  ShieldCheck, ShieldAlert, ShieldX, Shield, Clock, Calendar,
  Server, Lock, KeyRound, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const initialFormData = {
  host: '',
  port: 443,
  subject_cn: '',
  issuer_cn: '',
  valid_from: '',
  valid_until: '',
  days_remaining: '',
  signature_algorithm: 'sha256WithRSAEncryption',
  key_size: 2048,
  is_self_signed: 0,
  serial_number: '',
};

// Status badge component
const StatusBadge = ({ isExpired, isExpiringSoon, daysRemaining }) => {
  if (isExpired) {
    return (
      <span className="badge bg-red-500/20 text-red-400 border-red-500/30">
        <ShieldX className="h-3 w-3 mr-1" />
        Expirado
      </span>
    );
  }
  if (isExpiringSoon || (daysRemaining && daysRemaining <= 30)) {
    return (
      <span className="badge bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
        <ShieldAlert className="h-3 w-3 mr-1" />
        Por vencer
      </span>
    );
  }
  return (
    <span className="badge bg-green-500/20 text-green-400 border-green-500/30">
      <ShieldCheck className="h-3 w-3 mr-1" />
      Vigente
    </span>
  );
};

// Days remaining badge
const DaysRemainingBadge = ({ days }) => {
  if (days === null || days === undefined) return <span className="text-muted-foreground">-</span>;
  
  let colorClass = 'text-green-400';
  if (days <= 0) colorClass = 'text-red-400';
  else if (days <= 30) colorClass = 'text-yellow-400';
  else if (days <= 60) colorClass = 'text-orange-400';
  
  return (
    <span className={`font-medium ${colorClass}`}>
      {days <= 0 ? 'Expirado' : `${days} días`}
    </span>
  );
};

const Certificates = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions('assets', 'certificates');
  
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, valid: 0, expiring: 0, expired: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    host: '',
    issuer: 'all',
    status: 'all',
  });
  
  // Active stat filter (for clickable cards)
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'valid_until', dir: 'asc' });

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };
  
  // Form options
  const [formOptions, setFormOptions] = useState({ issuers: [], algorithms: [], key_sizes: [], ports: [] });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/certificates/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/certificates/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      
      if (filters.host) params.append('host_filter', filters.host);
      if (filters.issuer && filters.issuer !== 'all') params.append('issuer_filter', filters.issuer);
      if (filters.status && filters.status !== 'all') params.append('status_filter', filters.status);

      const response = await api.get(`/assets/certificates?${params}`);
      setCertificates(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar certificados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFormOptions(); fetchStats(); }, []);
  useEffect(() => { fetchCertificates(); }, [pagination.page, pagination.limit, sortConfig]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchCertificates();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset stat filter when using inline filters
    if (field === 'status') {
      setActiveStatFilter(value === 'all' ? 'all' : value);
    }
  };

  // Stat card click handler
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    // Map stat filter to status filter
    const statusMap = {
      'all': 'all',
      'valid': 'valid',
      'expiring': 'expiring',
      'expired': 'expired'
    };
    setFilters(prev => ({ ...prev, status: statusMap[filterType] || 'all' }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (cert) => {
    setEditingItem(cert);
    setFormData({
      host: cert.host || '',
      port: cert.port || 443,
      subject_cn: cert.subject_cn || '',
      issuer_cn: cert.issuer_cn || '',
      valid_from: cert.valid_from ? cert.valid_from.split('T')[0] : '',
      valid_until: cert.valid_until ? cert.valid_until.split('T')[0] : '',
      days_remaining: cert.days_remaining || '',
      signature_algorithm: cert.signature_algorithm || 'sha256WithRSAEncryption',
      key_size: cert.key_size || 2048,
      is_self_signed: cert.is_self_signed || 0,
      serial_number: cert.serial_number || '',
    });
    setModalOpen(true);
  };

  const handleCopy = (cert) => {
    setEditingItem(null);
    setFormData({
      host: '', // Unique field - empty
      port: cert.port || 443,
      subject_cn: '', // Unique field - empty
      issuer_cn: cert.issuer_cn || '',
      valid_from: '',
      valid_until: '',
      days_remaining: '',
      signature_algorithm: cert.signature_algorithm || 'sha256WithRSAEncryption',
      key_size: cert.key_size || 2048,
      is_self_signed: cert.is_self_signed || 0,
      serial_number: '',
    });
    setCopyModalOpen(true);
  };

  const handleView = (cert) => {
    setViewingItem(cert);
    setDetailModalOpen(true);
  };

  const handleSave = async (isCopy = false) => {
    if (!formData.host.trim()) {
      toast.error('El host es requerido');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        port: parseInt(formData.port) || 443,
        key_size: parseInt(formData.key_size) || 2048,
        days_remaining: formData.days_remaining ? parseInt(formData.days_remaining) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
      };

      if (editingItem && !isCopy) {
        await api.put(`/assets/certificates/${editingItem.id}`, payload);
        toast.success('Certificado actualizado exitosamente');
      } else {
        await api.post('/assets/certificates', payload);
        toast.success('Certificado creado exitosamente');
      }
      setModalOpen(false);
      setCopyModalOpen(false);
      fetchCertificates();
      fetchStats(); // Refresh stats after create/edit
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cert) => {
    try {
      await api.delete(`/assets/certificates/${cert.id}`);
      toast.success('Certificado eliminado exitosamente');
      setDeleteConfirm(null);
      fetchCertificates();
      fetchStats(); // Refresh stats after delete
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar';
      toast.error(msg);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Certificados SSL</h1>
            <p className="page-subtitle">Gestión de certificados digitales</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/certificates"
              filters={{ search: filters.search }}
              filename="certificados"
            />
            <Button 
              className={`btn btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={canCreate ? handleCreate : undefined}
              disabled={!canCreate}
              title={!canCreate ? 'Sin permiso para crear' : undefined}
              data-testid="add-certificate-btn"
            >
              <Plus className="h-4 w-4" />
              Nuevo Certificado
            </Button>
          </div>
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="certificates-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Shield className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'valid' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('valid')}
            data-testid="stats-valid"
          >
            <ShieldCheck className="h-6 w-6 text-green-400 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-400">{stats.valid}</div>
              <div className="stats-card-label">Vigentes</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${activeStatFilter === 'expiring' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatFilter('expiring')}
            data-testid="stats-expiring"
          >
            <ShieldAlert className="h-6 w-6 text-yellow-400 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-400">{stats.expiring}</div>
              <div className="stats-card-label">Por vencer</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${activeStatFilter === 'expired' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('expired')}
            data-testid="stats-expired"
          >
            <ShieldX className="h-6 w-6 text-red-400 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-400">{stats.expired}</div>
              <div className="stats-card-label">Expirados</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('host')}
                        >
                          <div className="flex items-center gap-1">
                            HOST
                            <SortIcon field="host" />
                          </div>
                        </th>
                        <th>PUERTO</th>
                        <th>SUBJECT (CN)</th>
                        <th>EMISOR</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('valid_until')}
                        >
                          <div className="flex items-center gap-1">
                            VÁLIDO HASTA
                            <SortIcon field="valid_until" />
                          </div>
                        </th>
                        <th>DÍAS RESTANTES</th>
                        <th>ESTADO</th>
                        <th className="w-28 text-right">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={filters.host}
                            onChange={(e) => handleFilterChange('host', e.target.value)}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select value={filters.issuer} onValueChange={(v) => handleFilterChange('issuer', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {formOptions.issuers.map(issuer => (
                                <SelectItem key={issuer} value={issuer}>{issuer}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              <SelectItem value="valid">Vigentes</SelectItem>
                              <SelectItem value="expiring">Por vencer</SelectItem>
                              <SelectItem value="expired">Expirados</SelectItem>
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {certificates.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron certificados</p>
                            </div>
                          </td>
                        </tr>
                      ) : certificates.map((cert) => (
                        <tr key={cert.id} data-testid={`cert-row-${cert.host}`}>
                          <td className="font-medium text-primary">{cert.host}</td>
                          <td className="text-muted-foreground">{cert.port}</td>
                          <td className="text-muted-foreground max-w-[200px] truncate" title={cert.subject_cn}>
                            {cert.subject_cn || '-'}
                          </td>
                          <td className="text-muted-foreground max-w-[150px] truncate" title={cert.issuer_cn}>
                            {cert.issuer_cn || '-'}
                          </td>
                          <td className="text-muted-foreground">{formatDate(cert.valid_until)}</td>
                          <td><DaysRemainingBadge days={cert.days_remaining} /></td>
                          <td>
                            <StatusBadge 
                              isExpired={cert.is_expired} 
                              isExpiringSoon={cert.is_expiring_soon}
                              daysRemaining={cert.days_remaining}
                            />
                          </td>
                          <td className="w-28 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button 
                                onClick={() => handleView(cert)}
                                className="action-btn" 
                                title="Ver detalle"
                                data-testid={`view-cert-${cert.host}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => canUpdate && handleEdit(cert)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={canUpdate ? "Editar" : "Sin permiso para editar"}
                                disabled={!canUpdate}
                                data-testid={`edit-cert-${cert.host}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => canCreate && handleCopy(cert)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={canCreate ? "Copiar registro" : "Sin permiso para crear"}
                                disabled={!canCreate}
                                data-testid={`copy-cert-${cert.host}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => canDelete && setDeleteConfirm(cert)}
                                className={`action-btn-delete ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={canDelete ? "Eliminar" : "Sin permiso para eliminar"}
                                disabled={!canDelete}
                                data-testid={`delete-cert-${cert.host}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      {pagination.total > 0 ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}` : '0 resultados'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pagination.limit.toString()}
                        onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-2xl">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Certificado' : 'Nuevo Certificado'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Host *</Label>
                <Input 
                  value={formData.host} 
                  onChange={(e) => handleInputChange('host', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="ej: www.ejemplo.com"
                  data-testid="input-host"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Puerto</Label>
                <Select value={String(formData.port)} onValueChange={(v) => handleInputChange('port', v)}>
                  <SelectTrigger className="input-subtle mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.ports.map(p => (
                      <SelectItem key={p} value={String(p)}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Subject (CN)</Label>
                <Input 
                  value={formData.subject_cn} 
                  onChange={(e) => handleInputChange('subject_cn', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="Common Name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Emisor (CN)</Label>
                <Input 
                  value={formData.issuer_cn} 
                  onChange={(e) => handleInputChange('issuer_cn', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="Autoridad certificadora"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Válido Desde</Label>
                <Input 
                  type="date"
                  value={formData.valid_from} 
                  onChange={(e) => handleInputChange('valid_from', e.target.value)} 
                  className="input-subtle mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Válido Hasta</Label>
                <Input 
                  type="date"
                  value={formData.valid_until} 
                  onChange={(e) => handleInputChange('valid_until', e.target.value)} 
                  className="input-subtle mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Algoritmo</Label>
                <Select value={formData.signature_algorithm} onValueChange={(v) => handleInputChange('signature_algorithm', v)}>
                  <SelectTrigger className="input-subtle mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.algorithms.length > 0 ? formOptions.algorithms.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    )) : (
                      <SelectItem value="sha256WithRSAEncryption">sha256WithRSAEncryption</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Tamaño de Clave</Label>
                <Select value={String(formData.key_size)} onValueChange={(v) => handleInputChange('key_size', v)}>
                  <SelectTrigger className="input-subtle mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.key_sizes.map(k => (
                      <SelectItem key={k} value={String(k)}>{k} bits</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm font-medium">Número de Serie</Label>
                <Input 
                  value={formData.serial_number} 
                  onChange={(e) => handleInputChange('serial_number', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="Serial number"
                />
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={() => handleSave(false)} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="modal-content max-w-2xl">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar Certificado
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 space-y-4">
              {/* Unique fields */}
              <div className="border-l-4 border-yellow-500 pl-4 space-y-4">
                <p className="text-xs text-yellow-500 font-medium uppercase">Campos únicos (requeridos)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Host *</Label>
                    <Input 
                      value={formData.host} 
                      onChange={(e) => handleInputChange('host', e.target.value)} 
                      className="input-subtle mt-1.5 border-yellow-500/50" 
                      placeholder="ej: www.ejemplo.com"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Subject (CN)</Label>
                    <Input 
                      value={formData.subject_cn} 
                      onChange={(e) => handleInputChange('subject_cn', e.target.value)} 
                      className="input-subtle mt-1.5 border-yellow-500/50" 
                      placeholder="Common Name"
                    />
                  </div>
                </div>
              </div>
              {/* Copied fields */}
              <div className="border-l-4 border-green-500 pl-4 space-y-4">
                <p className="text-xs text-green-500 font-medium uppercase">Campos copiados</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Puerto</Label>
                    <Select value={String(formData.port)} onValueChange={(v) => handleInputChange('port', v)}>
                      <SelectTrigger className="input-subtle mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formOptions.ports.map(p => (
                          <SelectItem key={p} value={String(p)}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Emisor (CN)</Label>
                    <Input 
                      value={formData.issuer_cn} 
                      onChange={(e) => handleInputChange('issuer_cn', e.target.value)} 
                      className="input-subtle mt-1.5" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Algoritmo</Label>
                    <Input value={formData.signature_algorithm} className="input-subtle mt-1.5" readOnly />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Tamaño de Clave</Label>
                    <Input value={`${formData.key_size} bits`} className="input-subtle mt-1.5" readOnly />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setCopyModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving}>
                {saving ? 'Creando...' : 'Crear Copia'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="modal-content max-w-2xl">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Detalle del Certificado
              </DialogTitle>
            </DialogHeader>
            {viewingItem && (
              <div className="modal-body py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">{viewingItem.host}:{viewingItem.port}</h3>
                  <StatusBadge 
                    isExpired={viewingItem.is_expired} 
                    isExpiringSoon={viewingItem.is_expiring_soon}
                    daysRemaining={viewingItem.days_remaining}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <div>
                      <span className="text-muted-foreground">Subject (CN):</span>
                      <p className="font-medium">{viewingItem.subject_cn || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Subject completo:</span>
                      <p className="font-medium text-xs break-all">{viewingItem.subject_full || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">SAN:</span>
                      <p className="font-medium text-xs break-all">{viewingItem.san || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Válido desde:</span>
                      <p className="font-medium">{formatDate(viewingItem.valid_from)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Válido hasta:</span>
                      <p className="font-medium">{formatDate(viewingItem.valid_until)}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-muted-foreground">Emisor (CN):</span>
                      <p className="font-medium">{viewingItem.issuer_cn || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Emisor completo:</span>
                      <p className="font-medium text-xs break-all">{viewingItem.issuer_full || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Algoritmo:</span>
                      <p className="font-medium">{viewingItem.signature_algorithm || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Tamaño de clave:</span>
                      <p className="font-medium">{viewingItem.key_size ? `${viewingItem.key_size} bits` : '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Auto-firmado:</span>
                      <p className="font-medium">{viewingItem.is_self_signed ? 'Sí' : 'No'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 border-t border-border/30">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Serial:</span>
                      <p className="font-mono text-xs break-all">{viewingItem.serial_number || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Thumbprint SHA256:</span>
                      <p className="font-mono text-xs break-all">{viewingItem.thumbprint_sha256 || '-'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 border-t border-border/30 text-xs text-muted-foreground">
                  <p>Último escaneo: {formatDate(viewingItem.last_scan)}</p>
                </div>
              </div>
            )}
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                ¿Está seguro que desea eliminar el certificado <span className="font-semibold text-foreground">{deleteConfirm?.host}</span>?
              </p>
              <p className="text-sm text-destructive mt-2">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Certificates;
